"use client"

import Breadcrumbs from "@mui/material/Breadcrumbs"
import { STEPS_EN } from "components/shopFeatures/wizard/constants"
import usePrivacyPolicyLink from "hooks/usePrivacyPolicyLink"
import { usePathname } from "next/navigation"
import styled from "styled-components"
import BreadcrumbsIcon from "./../reusable/icons/BreadCrumbsIcon"
import { BreadCrumbsData } from "./BreadCrumbsData"
import NextLink from "./bit/NextLink"
import { safeDecodeSlug } from "./bit/utils/decodeParam"
import slugify from "./bit/utils/slugify"

const SINGLE_SERVICE = "singleService"
const CONFIGURATOR = "configurator"

const StyledInnerWrapper = styled.div<{ $page?: string }>`
  ${(p) => p.$page === SINGLE_SERVICE && "top: -20px"};
  ${(p) => p.$page === SINGLE_SERVICE && "right: 25px"};

  ${(p) => p.theme.breakpoints.down("sm")} {
    position: absolute;
    ${(p) => p.$page !== CONFIGURATOR && "right: 50px;"};
    ${(p) => p.$page === SINGLE_SERVICE && "top: -10px"};
    ${(p) => p.$page === SINGLE_SERVICE && "right: 0px"};
  }
`

const StyledWrapper = styled.div<{ $page?: string; $pathname?: string }>`
  ${(p) => p?.$page !== CONFIGURATOR && `width: 100%;`}
  max-width: 1400px;

  ${(p) => p.theme.breakpoints.up("md")} {
    ${(p) => p.$page === CONFIGURATOR && "margin-top: -50px"};
    ${(p) =>
      p.$page === CONFIGURATOR &&
      p?.$pathname !== STEPS_EN.summary &&
      "margin-right: 50px"};
    display: flex;
    justify-content: flex-start;
    padding-right: 20px;
  }

  ${(p) => p.theme.breakpoints.down("sm")} {
    ${(p) => p.$page !== CONFIGURATOR && "padding-right: 15px;"};
    ${(p) => p.$pathname === STEPS_EN.color && "padding-right: 16px"};
  }
  svg {
    margin-top: 2px;
  }
`

const StyledLink = styled(NextLink)`
  color: ${(p) => p.theme.colors.disable};
  font-size: 10px;
  font-weight: 300;
  letter-spacing: normal;
`

type BreadCrumbsType = {
  name?: string
  modelName?: string
  id?: number
  page?: string
  pathname?: string
}

const BreadCrumbsWrapper = ({
  name,
  modelName,
  id,
  page,
  pathname,
}: BreadCrumbsType) => {
  const path = usePathname()

  // Example: /service-center/center-slug → extract last part as slug
  const pathSegments = path?.split("/") || []
  const serviceCenterSlug = pathSegments[pathSegments.length - 1]

  const carLinkUrl = () => {
    if (!id || !modelName) return "/"
    return `/model/${id}/${slugify(modelName)}`
  }

  const privacyPolicyLink = usePrivacyPolicyLink()
  const breadcrumbsWithPrivacyPolicy = {
    ...BreadCrumbsData,
    PrivacyPolicy: {
      links: [
        {
          title: "מדיניות הפרטיות",
          link: privacyPolicyLink,
        },
      ],
    },
  }

  return (
    <StyledWrapper $page={page} $pathname={pathname}>
      <StyledInnerWrapper $page={page}>
        {name && breadcrumbsWithPrivacyPolicy[name] && !modelName && !id && (
          <Breadcrumbs
            separator={<BreadcrumbsIcon />}
            aria-label="מיקומך בדפי האתר"
          >
            <StyledLink color="inherit" href="/">
              ראשי
            </StyledLink>

            {breadcrumbsWithPrivacyPolicy[name].links.map((item, index) => (
              <StyledLink
                key={index}
                color="inherit"
                href={item.link}
                aria-current={
                  index ===
                    breadcrumbsWithPrivacyPolicy[name].links.length - 1 &&
                  page !== SINGLE_SERVICE
                    ? "page"
                    : "step"
                }
              >
                {item.title}
              </StyledLink>
            ))}

            {page === SINGLE_SERVICE && (
              <StyledLink color="inherit" href={path} aria-current="page">
                {safeDecodeSlug(serviceCenterSlug?.replace(/-/g, " "))}
              </StyledLink>
            )}
          </Breadcrumbs>
        )}

        {modelName && id && (
          <Breadcrumbs
            separator={<BreadcrumbsIcon />}
            aria-label="מיקומך בדפי האתר"
          >
            <StyledLink color="inherit" href="/">
              ראשי
            </StyledLink>

            <StyledLink color="inherit" href="/models">
              לכל הדגמים
            </StyledLink>

            <StyledLink
              href={carLinkUrl()}
              aria-current={page !== CONFIGURATOR ? "page" : "step"}
            >
              {modelName}
            </StyledLink>

            {page === CONFIGURATOR && (
              <StyledLink href={path} aria-current="page">
                התאמה אישית ורכישה
              </StyledLink>
            )}
          </Breadcrumbs>
        )}
      </StyledInnerWrapper>
    </StyledWrapper>
  )
}

export default BreadCrumbsWrapper
